import * as React from 'react';
import * as styles from './Details.css';
import { LocalizationService } from '../../../services/LocalizationService';
import { IAPersonaSmall } from '../../personaSmall/PersonaSmall';
import { IACroppedImage } from '../../imageCropper/CroppedImage';
import { IAImageCropper } from '../../imageCropper/ImageCropper';
import { IAButton } from '../../button/Button';
import UserService from '../../../services/UserService';
import ImageService from '../../../services/ImageService';
import ContentOrganizerService from '../../../services/ContentOrganizerService';
import { IUser } from '../../../interfaces/IUser';
import { ContentOrganizerAdminStore } from '../stores/ContentOrganizerAdminStore';
import { Helper } from '../../../Helper';
import { Environment } from '../../../services/Environment';
import { inject, observer } from 'mobx-react';
import { IATooltip } from '../../tooltip/Tooltip';
import { IAIcon } from '../../icon/Icon';
import { ContentOrganizerSiteUrlSyncPanel } from './ContentOrganizerSiteUrlSyncPanel';
import { IAMessageBar } from '../../messageBar/MessageBar';
import { IAMediaSelector } from '../../mediaSelector/MediaSelector';

const TimeZone = require('../../contentOrganizerWizard/panels/TimeZones.json');
const urlAdressPrefix: string = '.../sites/';

interface IMessage {
  id?: string,
  description: string,
  type: "warning" | "success" | "error" | "information",
  fixHandler?: () => void,
  closeHandler?: () => void,
}

export interface IDetailsProps {
  contentOrganizerAdminStore?: ContentOrganizerAdminStore;
}

export interface IDetailsState {
  siteOwners: IUser[];
  messages: IMessage[];
  showEditorImageCropper: boolean;
  showEditorImageCropperErrorMessage: boolean;
  showSiteUrlSyncPanel: boolean;

}

@inject('contentOrganizerAdminStore')
@observer
export class Details extends React.Component<IDetailsProps, IDetailsState> {

  private localizationService = new LocalizationService();

  constructor(props: IDetailsProps) {
    super(props);
    this.state = {
      siteOwners: [],
      messages: [],
      showEditorImageCropper: false,
      showEditorImageCropperErrorMessage: false,
      showSiteUrlSyncPanel: false,
    };
    this.props.contentOrganizerAdminStore.isMobile = window.innerWidth < 667;
  }

  public componentDidMount() {
    this.checkForRequiredReload();
    this.getSiteOwnersInfo();
    this.getHealtStatusWarnings();
  }

  public componentWillUnmount() {
    this.resetDetails();
  }

  private resetDetails(): void {

  }

  private getLanguageName(): string {
    const SupportedLanguages = [
      { key: "da-DK", value: this.localizationService.strings.ContentOrganizer_Danish },
      { key: "de-DE", value: this.localizationService.strings.ContentOrganizer_German },
      { key: "en-US", value: this.localizationService.strings.ContentOrganizer_English },
      { key: "pt-BR", value: this.localizationService.strings.ContentOrganizer_Portuguese },
      { key: "sv-SE", value: this.localizationService.strings.ContentOrganizer_Swedish }
    ];
    const siteLanguage = SupportedLanguages.find(x => x.key === this.props.contentOrganizerAdminStore.selectedInstance?.settings.siteLanguage);
    return siteLanguage?.value;
  }

  private getTimezoneDescription(): string {
    const timezone = TimeZone.find(x => x.key === this.props.contentOrganizerAdminStore.selectedInstance?.settings.timeZoneId);
    return timezone?.value;
  }

  private async getSiteOwnersInfo() {
    const siteOwnerUsers: IUser[] = [];
    let siteOwnerEmails = this.props.contentOrganizerAdminStore.selectedInstance?.settings.siteOwnersWithGroups;

    if (siteOwnerEmails.length > 0) {
      for (const user of siteOwnerEmails) {
        const userResult = await UserService.getUsers(this.props.contentOrganizerAdminStore.environment, this.props.contentOrganizerAdminStore.token, `${this.props.contentOrganizerAdminStore.tenant}.sharepoint.com`, user.userPrincipalName);
        const siteOwnerUser: IUser = userResult.users.length > 0 ? userResult.users[0] : user;
        siteOwnerUsers.push(siteOwnerUser);
      }
      this.setState({ siteOwners: siteOwnerUsers });
    }
  }

  private redirectToSite(): void {
    window.open(`https://${this.props.contentOrganizerAdminStore.tenant}.sharepoint.com/sites/${this.props.contentOrganizerAdminStore.selectedInstance?.settings.siteCollection}`, '_blank');
  }

  private renderPersonaSmall(user: IUser): JSX.Element {
    return (
      <IAPersonaSmall
        name={user.displayName}
        profileImage={Helper.getProfileImageUrlFromImageAPI(Environment.getEnvironmentForImageAPI(this.props.contentOrganizerAdminStore.environment),
          this.props.contentOrganizerAdminStore.tenant, this.props.contentOrganizerAdminStore.token, user.userPrincipalName)}
        styles={{ width: "auto", marginRight: "10px", marginTop: "5px", marginBottom: "30px" }}
      />
    )
  }

  private getActivityErrorLabel(activityName: string) {
    switch (activityName) {
      case "SetupPermissions":
        return this.localizationService.strings.ContentOrganizerAdminInstance_SetupPermissionsError;
      default:
        break;
    }
  }

  private checkForRequiredReload() {
    if (this.getQueryStringParams()._reload === 'true') {
      window.location.href = window.location.href
        .replace('?_reload=true', '')
        .replace('&_reload=true', '');
    }
  }

  private async getHealtStatusWarnings() {
    const messages: IMessage[] = [];

    if (!this.props.contentOrganizerAdminStore.selectedInstance) {
      return;
    }

    var healthStatus = await ContentOrganizerService.getContentOrganizerHealthStatus(this.props.contentOrganizerAdminStore.environment, this.props.contentOrganizerAdminStore.token, this.props.contentOrganizerAdminStore.tenant, this.props.contentOrganizerAdminStore.selectedInstance.settings.siteCollection)

    if (healthStatus.siteUrlSyncRequired) {
      messages.push({
        type: "warning",
        description: this.localizationService.strings.ContentOrganizerAdminInstance_HealthStatusSiteUrlSyncRequired,
        fixHandler: () => {
          this.setState({ showSiteUrlSyncPanel: true });
        }
      });
    }

    if (this.getQueryStringParams()._invoked_by === 'site_url_sync') {
      messages.push({
        id: "site_url_sync_finished",
        type: "success",
        description: this.localizationService.strings.ContentOrganizerAdminInstance_SiteUrlSyncCompleted,
        closeHandler: () => {
          this.removeMessageById("site_url_sync_finished");
        }
      });
    }

    this.setState({ messages });
  }

  private removeMessageById(id: string): void {
    this.setState({ messages: [...this.state.messages].filter(message => message.id != id) });
  }

  private getQueryStringParams(url?: string): any {

    // get query string from url (optional) or window
    var queryString = url ? url.split('?')[1] : window.location.search.slice(1);

    // we'll store the parameters here
    var obj = {};

    // if query string exists
    if (queryString) {

      // stuff after # is not part of query string, so get rid of it
      queryString = queryString.split('#')[0];

      // split our query string into its component parts
      var arr = queryString.split('&');

      for (var i = 0; i < arr.length; i++) {
        // separate the keys and the values
        var a = arr[i].split('=');

        // set parameter name and value (use 'true' if empty)
        var paramName = a[0];
        var paramValue = typeof (a[1]) === 'undefined' ? true : a[1];

        // (optional) keep case consistent
        paramName = paramName.toLowerCase();
        if (typeof paramValue === 'string') paramValue = paramValue.toLowerCase();

        // if the paramName ends with square brackets, e.g. colors[] or colors[2]
        if (paramName.match(/\[(\d+)?\]$/)) {

          // create key if it doesn't exist
          var key = paramName.replace(/\[(\d+)?\]/, '');
          if (!obj[key]) obj[key] = [];

          // if it's an indexed array e.g. colors[2]
          if (paramName.match(/\[\d+\]$/)) {
            // get the index value and add the entry at the appropriate position
            var index = /\[(\d+)\]/.exec(paramName)[1];
            obj[key][index] = paramValue;
          } else {
            // otherwise add the value to the end of the array
            obj[key].push(paramValue);
          }
        } else {
          // we're dealing with a string
          if (!obj[paramName]) {
            // if it doesn't exist, create property
            obj[paramName] = paramValue;
          } else if (obj[paramName] && typeof obj[paramName] === 'string') {
            // if property does exist and it's a string, convert it to an array
            obj[paramName] = [obj[paramName]];
            obj[paramName].push(paramValue);
          } else {
            // otherwise add the property
            obj[paramName].push(paramValue);
          }
        }
      }
    }

    return obj;
  }

  public render(): JSX.Element {
    const TemplateOptionsTranslationMap = [
      { "Handbook": this.localizationService.strings.ContentOrganizer_Handbook },
      { "Basic": this.localizationService.strings.ContentOrganizer_BasicTemplate },
      { "NoTemplate": this.localizationService.strings.ContentOrganizer_NoTemplate },
    ];
    return (
      <div>
        {this.state.messages.map((message) => (
          <IAMessageBar
            type={message.type}
            onDismiss={message.closeHandler}
            icon="Attention"
            content={message.fixHandler
              ? <>{message.description}&nbsp;<span style={{ cursor: "pointer", fontWeight: "bold" }} onClick={() => { message.fixHandler(); }}>{this.localizationService.strings.ContentOrganizerAdminInstance_HealthStatusFixIssue}</span></>
              : <>{message.description}</>
            }
            showIcon={true}
            showCloseButton={!!message.closeHandler}
            style={{
              marginLeft: 20,
              width: "calc(100% - 40px)"
            }}
          />
        ))}
        <div className={styles.ContentOrganizerDetail_leftColumn}>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_Title}</div>
            <div>{this.props.contentOrganizerAdminStore.selectedInstance?.settings.title}</div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_URLAddress}</div>
            <div
              style={{ textDecoration: "underline", color: "blue", cursor: "pointer" }}
              onClick={() => this.redirectToSite()}
            >
              {urlAdressPrefix}{this.props.contentOrganizerAdminStore.selectedInstance?.settings.siteCollection}
            </div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_Description}</div>
            <div>{this.props.contentOrganizerAdminStore.selectedInstance?.settings.description}</div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_Timezone}</div>
            <div>{this.getTimezoneDescription()}</div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_HubAssociation}</div>
            <div>{this.props.contentOrganizerAdminStore.selectedInstance?.settings.hubSite?.isFeatureSelected ? this.props.contentOrganizerAdminStore.selectedInstance?.settings.hubSite?.title : this.localizationService.strings.ContentOrganizer_PlaceholderNone}</div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_Language}</div>
            <div>{this.getLanguageName()}</div>
          </div>
          {((Object.keys(this.props.contentOrganizerAdminStore?.selectedInstance?.durableFunctionOutput?.errors || {}).length > 0) &&
            <div className={styles.ContentOrganizerDetail_row}>
              <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_CompletedWithErrors}</div>
              <ul>
                {Object.keys(this.props.contentOrganizerAdminStore?.selectedInstance?.durableFunctionOutput?.errors || {}).map((activityName) => (
                  this.props.contentOrganizerAdminStore?.selectedInstance?.durableFunctionOutput.errors[activityName].map((error) =>
                    <li className={styles.ContentOrganizerDetail_small}>{this.getActivityErrorLabel(activityName)}: <strong>${error}</strong></li>
                  )
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className={styles.ContentOrganizerDetail_rightColumn}>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_SiteAdministrator}</div>
            {
              this.state.siteOwners?.length > 0
                ? this.state.siteOwners.map(siteOwnerInfo => (
                  this.renderPersonaSmall(siteOwnerInfo)
                ))
                : this.props.contentOrganizerAdminStore.selectedInstance?.settings.siteOwnersWithGroups?.length > 0
                  ? this.props.contentOrganizerAdminStore.selectedInstance?.settings.siteOwnersWithGroups?.map(siteOwnerEmail => (
                    this.renderPersonaSmall(siteOwnerEmail)
                  ))
                  : <div>{this.localizationService.strings.ContentOrganizerAdminInstance_CannotLoadData}</div>
            }
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_Template}</div>
            <div>{this.props.contentOrganizerAdminStore.selectedInstance?.settings.template
                ? TemplateOptionsTranslationMap[this.props.contentOrganizerAdminStore.selectedInstance?.settings.template]
                : "-"}</div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label} style={{ width: "max-content" }}>
              {this.localizationService.strings.ContentOrganizerAdminInstance_UseTags}
              <div style={{ position: 'relative', left: '5px', float: "right" }}>
                <IATooltip
                  content="Tooltip"
                  type={"bubble"}
                  styles={{
                    width: 300,
                    color: "#999999",
                    textAlign: "center",
                    marginBottom: "5px"
                  }}
                  componentContent={
                    <div>
                      <ul>
                        <li>{this.localizationService.strings.ContentOrganizerAdminInstance_Info}</li>
                      </ul>
                    </div>
                  }>
                  <IAIcon
                    title="Info"
                    size={17}
                    color={"#999999"}
                    style={{
                      float: "left"
                    }} />
                </IATooltip>
              </div>
            </div>
            <div>{this.props.contentOrganizerAdminStore.selectedInstance?.settings.metadata?.isFeatureSelected ? this.localizationService.strings.ContentOrganizerAdminInstance_Yes : this.localizationService.strings.ContentOrganizerAdminInstance_No}</div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label} style={{ width: "max-content" }}>
              {this.localizationService.strings.ContentOrganizerAdminInstance_UseRevisionDate}
              <div style={{ position: 'relative', left: '5px', float: "right" }}>
                <IATooltip
                  content="Tooltip"
                  type={"bubble"}
                  styles={{
                    width: 300,
                    color: "#999999",
                    textAlign: "center",
                    marginBottom: "5px"
                  }}
                  componentContent={
                    <div>
                      <ul>
                        <li>{this.localizationService.strings.ContentOrganizerAdminInstance_Info}</li>
                      </ul>
                    </div>
                  }>
                  <IAIcon
                    title="Info"
                    size={17}
                    color={"#999999"}
                    style={{
                      float: "left"
                    }} />
                </IATooltip>
              </div>
            </div>
            <div>{this.props.contentOrganizerAdminStore.selectedInstance?.settings.revision?.isFeatureSelected ? this.localizationService.strings.ContentOrganizerAdminInstance_Yes : this.localizationService.strings.ContentOrganizerAdminInstance_No}</div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label} style={{ width: "max-content" }}>
              {this.localizationService.strings.ContentOrganizerAdminInstance_UseContentResponsible}
              <div style={{ position: 'relative', left: '5px', float: "right" }}>
                <IATooltip
                  content="Tooltip"
                  type={"bubble"}
                  styles={{
                    width: 300,
                    color: "#999999",
                    textAlign: "center",
                    marginBottom: "5px"
                  }}
                  componentContent={
                    <div>
                      <ul>
                        <li>{this.localizationService.strings.ContentOrganizerAdminInstance_Info}</li>
                      </ul>
                    </div>
                  }>
                  <IAIcon
                    title="Info"
                    size={17}
                    color={"#999999"}
                    style={{
                      float: "left"
                    }} />
                </IATooltip>
              </div>
            </div>
            <div>{this.props.contentOrganizerAdminStore.selectedInstance?.settings.responsible?.isFeatureSelected ? this.localizationService.strings.ContentOrganizerAdminInstance_Yes : this.localizationService.strings.ContentOrganizerAdminInstance_No}</div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_Navigator}</div>
            <div>{this.props.contentOrganizerAdminStore.selectedInstance?.settings.navigator?.isFeatureSelected ? this.props.contentOrganizerAdminStore.selectedInstance?.settings.navigator?.instance?.title : this.localizationService.strings.ContentOrganizer_PlaceholderNone}</div>
          </div>
          <div className={styles.ContentOrganizerDetail_row}>
            <div className={styles.ContentOrganizerDetail_label}>{this.localizationService.strings.ContentOrganizerAdminInstance_FeaturedImage}</div>
            <div className={styles.ContentOrganizerDetail_featuredImage}>
              {this.props.contentOrganizerAdminStore.selectedInstance.settings.featuredImageUrl && this.state.showEditorImageCropper ?
                <IAImageCropper
                  token={this.props.contentOrganizerAdminStore.token}
                  tenant={this.props.contentOrganizerAdminStore.tenant}
                  environment={this.props.contentOrganizerAdminStore.environment}
                  image={ImageService.toDirectUrl(this.props.contentOrganizerAdminStore.selectedInstance.settings.featuredImageUrl)}
                  showButtons={true}
                  enableZoom={true}
                  modified={undefined}
                  height={400}
                  initialCrop={undefined}
                  onUpdate={(crop) => console.log(crop)}
                  onSave={(crop) => {
                    ContentOrganizerService.updateFeaturedImage(
                      this.props.contentOrganizerAdminStore.environment,
                      this.props.contentOrganizerAdminStore.token,
                      this.props.contentOrganizerAdminStore.tenant,
                      this.props.contentOrganizerAdminStore.selectedInstance.settings.siteCollection,
                      {
                        featuredImageUrl: this.props.contentOrganizerAdminStore.selectedInstance.settings?.featuredImageUrl,
                        featuredImageCropSettings: crop
                      }
                    ).then(result => {
                      if (result) {
                        this.props.contentOrganizerAdminStore.selectedInstance.settings.featuredImageCropSettings = crop;
                        this.setState({ showEditorImageCropper: false });
                      }
                    });
                  }}
                  onCancel={() => this.setState({ showEditorImageCropper: false })}
                  onUnsupportedFormat={() => this.setState({ showEditorImageCropper: false, showEditorImageCropperErrorMessage: true })}
                />
                :
                <div style={{ position: "relative" }}>
                  {this.props.contentOrganizerAdminStore.selectedInstance.settings?.featuredImageUrl &&
                    <IAButton
                      buttonColor="rgba(0,0,0,.3)"
                      mouseOverButtonColor="rgba(0,0,0,.2)"
                      iconColor="#ffffff"
                      icon="Crop"
                      style={{
                        position: "absolute",
                        zIndex: 1
                      }}
                      borderRadius={5}
                      onClick={() => this.setState({ showEditorImageCropper: true })}
                    />
                  }
                  {this.props.contentOrganizerAdminStore.selectedInstance.settings?.featuredImageCropSettings && this.props.contentOrganizerAdminStore.selectedInstance.settings?.featuredImageUrl && (
                    <IACroppedImage
                      imageSrc={this.props.contentOrganizerAdminStore.selectedInstance.settings?.featuredImageUrl}
                      modified={undefined} // TODO
                      crop={this.props.contentOrganizerAdminStore.selectedInstance.settings?.featuredImageCropSettings}
                      token={this.props.contentOrganizerAdminStore.token}
                      tenant={this.props.contentOrganizerAdminStore.tenant}
                      environment={this.props.contentOrganizerAdminStore.environment}
                      marginTop={11}
                      minHeight={250}
                      showLoadingSpinner={true}
                    />
                  )}
                  <IAMediaSelector
                    isMobile={this.props.contentOrganizerAdminStore.isMobile}
                    fileType="image"
                    showPreview={!this.props.contentOrganizerAdminStore.selectedInstance.settings?.featuredImageCropSettings}
                    highlightColor={"blue"}
                    token={this.props.contentOrganizerAdminStore.token}
                    tenant={this.props.contentOrganizerAdminStore.tenant}
                    selectedImage={ImageService.toDirectUrl(this.props.contentOrganizerAdminStore.selectedInstance.settings.featuredImageUrl)}
                    environment={this.props.contentOrganizerAdminStore.environment}
                    uploadUrl={`${Environment.getEnvironmentContentOrganizerAPI(this.props.contentOrganizerAdminStore.environment)}/${this.props.contentOrganizerAdminStore.tenant}/image/upload?site=${this.props.contentOrganizerAdminStore.selectedInstance.settings.siteCollection}`}
                    onChange={(imageUrl: string) => {
                      ContentOrganizerService.updateFeaturedImage(
                        this.props.contentOrganizerAdminStore.environment,
                        this.props.contentOrganizerAdminStore.token,
                        this.props.contentOrganizerAdminStore.tenant,
                        this.props.contentOrganizerAdminStore.selectedInstance.settings.siteCollection,
                        {
                          featuredImageUrl: imageUrl
                        }
                      ).then(result => {
                        if (result) {
                          this.props.contentOrganizerAdminStore.selectedInstance.settings.featuredImageUrl = imageUrl
                        }
                      });
                    }}
                    style={{
                      margin: "15px 0"
                    }}
                  />
                </div>
              }
            </div>
            <div className={styles.ContentOrganizerDetail_label_imagedescription}>
              {this.localizationService.strings.ContentOrganizerAdminInstance_FeaturedImageSize}
            </div>
          </div>
        </div>
        <ContentOrganizerSiteUrlSyncPanel
          open={this.state.showSiteUrlSyncPanel}
          onClose={() => {
            this.setState({ showSiteUrlSyncPanel: false })
          }}
        />
      </div>
    );
  }
}